import { Box, SxProps, Grid } from "@mui-wrapper";
import { TabComponent } from "@components/tabs/tab-component";
import { PropsWithChildren, ReactNode } from "react";
import { TabItemProps } from "@components/tabs/tab-types";
import { RouteParamValues } from "@router/route-params";
import { OpenTabEvents } from "@utils/track-event/track-event-list";

interface Props<K extends RouteParamValues> {
  /** The header to show in the page */
  header?: ReactNode;

  /** The tabs and their content to show in the page */
  tabs?: TabItemProps<K>[];

  /** Zero-based index of the tab that should be opened/selected per default */
  selectedTab?: K;

  /**
   * The name of the event to track opening tab events.
   * In the same page all opening of a tab is tracked with the same event name,
   * and the tab name is sent as a prop.
   */
  trackingEventPageName?: OpenTabEvents;

  /** The sx to be applied to the content box */
  contentSx?: SxProps;
}

/**
 * Manages the placement of headers, tabs, footer etc.
 */
export function ContentLayout<K extends RouteParamValues>({
  header,
  tabs,
  selectedTab,
  children,
  trackingEventPageName,
  contentSx = {},
}: PropsWithChildren<Props<K>>): React.JSX.Element {
  return (
    <Grid
      container
      sx={{
        height: "100%",
        flexDirection: "column",
        // A wrapping column sizes its children by the widest content on the page, which moves the
        // header between tabs. Without wrapping every child is as wide as the page itself.
        flexWrap: "nowrap",
        minWidth: 0,
      }}
    >
      {/* With tabs, the header sits right below them, as the Dashboard does */}
      {!!header && !tabs && <header>{header}</header>}

      {!!tabs && (
        <TabComponent
          tabs={tabs}
          selectedTab={selectedTab}
          header={header && <header>{header}</header>}
          trackingEventPageName={trackingEventPageName}
        />
      )}

      {!!children && (
        <Box
          component="main"
          sx={{ flexBasis: 0, flexGrow: 1, overflowY: "auto", ...contentSx }}
        >
          {children}
        </Box>
      )}
    </Grid>
  );
}
